import type { Database } from '../../lib/database.types'

export type UserProfile = Database['public']['Tables']['user_profiles']['Row']
export type UserProfileInsert = Database['public']['Tables']['user_profiles']['Insert']
export type UserProfileUpdate = Database['public']['Tables']['user_profiles']['Update']

export interface NotificationPreferences {
    daily_reminder: boolean
    point_assignments: boolean
    reward_unlocked: boolean
}

export interface AuthState {
    user: UserProfile | null
    isAuthenticated: boolean
    loading: boolean
    error: string | null
}

// Pour la compatibilité avec l'ancien modèle
export interface User {
    id: string
    name: string
    email: string
    photoURL?: string // Alias for avatar_url for backward compatibility
    avatar_url?: string // Primary field aligned with other models
    language: string
    email_verified?: boolean // Statut de vérification email
    notification_preferences: NotificationPreferences
}

// Fonction helper pour convertir UserProfile en User
export const userProfileToUser = (profile: UserProfile): User => ({
    id: profile.id,
    name: profile.name,
    email: profile.email, // Temporaire - sera remplacé par l'email de auth.users
    photoURL: profile.avatar_url || undefined, // For backward compatibility
    avatar_url: profile.avatar_url || undefined,
    language: profile.language,
    email_verified: (profile as any).email_verified || false, // Cast temporaire en attendant la mise à jour du schéma
    notification_preferences: {
        daily_reminder: true, // Default value since not in database
        point_assignments: true, // Default value since not in database
        reward_unlocked: true, // Default value since not in database
    },
})

// Fonction helper pour convertir UserProfile en User avec email depuis auth.users
export const userProfileToUserWithAuthEmail = async (profile: UserProfile, authEmail?: string): Promise<User> => ({
    id: profile.id,
    name: profile.name,
    email: authEmail || profile.email, // Utiliser l'email de auth.users en priorité
    photoURL: profile.avatar_url || undefined,
    avatar_url: profile.avatar_url || undefined,
    language: profile.language,
    email_verified: (profile as any).email_verified || false,
    notification_preferences: {
        daily_reminder: true,
        point_assignments: true,
        reward_unlocked: true,
    },
})

// Fonction helper pour convertir User en UserProfileInsert
export const userToUserProfileInsert = (user: Omit<User, 'id'> & { id: string }): UserProfileInsert => ({
    id: user.id,
    name: user.name,
    email: user.email,
    avatar_url: user.avatar_url || user.photoURL || null,
    language: user.language,
})

// Fonction helper pour convertir les mises à jour User en UserProfileUpdate
export const userToUserProfileUpdate = (updates: Partial<User>): UserProfileUpdate => {
    const profileUpdate: UserProfileUpdate = {}

    if (updates.name !== undefined) profileUpdate.name = updates.name
    // Email n'est plus stocké dans user_profiles - on utilise auth.users.email comme source de vérité
    // if (updates.email !== undefined) profileUpdate.email = updates.email
    if (updates.avatar_url !== undefined) profileUpdate.avatar_url = updates.avatar_url || null
    if (updates.photoURL !== undefined) profileUpdate.avatar_url = updates.photoURL || null // Backward compatibility
    if (updates.language !== undefined) profileUpdate.language = updates.language

    // Ajouter updated_at automatiquement
    profileUpdate.updated_at = new Date().toISOString()

    return profileUpdate
} 